import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppTabs from '../../components/AppTabs';
import './styles.scss'
import { useDispatch, useSelector } from "react-redux";
import AppointmentsTable from "../../components/AppointmentsTable";
import AppointmentsChanges from "../../components/AppointmentsChanges";
import { loadEntities, updateEntity, entityCreating, openAppointment } from "../../store/entities/actions";
import { ClientEntity } from "../../store/entities/state";
import { getAppointmentsForClient, isLoadingEntities } from "../../store/entities/selectors";
import { getClientAppointmentsAsync, getClientAppointmentsChangesAsync, updateAppointmentAsync } from "../../ajax/requests";
import { getTotalPayments } from "../../shared/appointmint/appointment";
import { AppointmentData } from "../../models";
import { canEditSelfAppointments, canSeePrices, canSeeInvoices } from "../../store/session/selectors";
import Calendar, { CalendarEvent, DayClickEvent, YearChangeEvent } from 'rc-year-calendar';
import { getDateString, parseDateOnlyToLocalDate, toDateOnlyString } from "../../shared/utils";
import { Tabs, Tab } from '@material-ui/core';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { useCookies } from "react-cookie";
import { useServicesSettings } from "../../store/settings/hooks";
import ClientWaitingList from './ClientWaitingList';
import MultiAppointmentPaymentButton from "./MultiAppointmentPaymentButton";

export interface ClientAppointmentsProps {
    data: ClientEntity,
}

const ClientAppointments: React.FC<ClientAppointmentsProps> = ({
    data,
}) => {
    const dispatch = useDispatch()
    const appointments = useSelector(getAppointmentsForClient(data.id))
    const isLoading = useSelector(isLoadingEntities('appointments'))
    const canEdit = useSelector(canEditSelfAppointments)
    const canSeePrice = useSelector(canSeePrices)
    const canSeeInvoice = useSelector(canSeeInvoices)
    const t = useTranslations()
    const [cookies] = useCookies(['APPOINTMENTS_CALENDAR_COLORS'])
    const servicesSettings = useServicesSettings()

    const calendarColorMode = (cookies['APPOINTMENTS_CALENDAR_COLORS'] as 'professional' | 'service' | 'auto' | undefined) ?? 'auto'

    const [year, setYear] = useState(new Date().getFullYear())
    const [activeTab, setActiveTab] = useState<'appointments' | 'changes' | 'waitingList'>('appointments')

    const appointmentsSelected = useMemo(() => appointments
        .map(a => ({appointment: a, date: toDateOnlyString(a.date)}))
        .filter(a => (a.date ?? '').startsWith(year.toString()))
        .sort((a, b) => (a.date ?? '') > (b.date ?? '') ? 1 : -1)
        .map(a => a.appointment), [appointments, year])

    const onLoadMore = useCallback((offset: number) => {
        dispatch(loadEntities(
            'appointments',
            pageSize => getClientAppointmentsAsync(data.id, new Date(`${year}`), new Date(`${year + 1}`), pageSize, offset),
            offset ? 'AppendResult' : 'NewResult',
            e => !offset && e.clientId === data.id))
    }, [year, data, dispatch])

    useEffect(() => onLoadMore(0), [])

    const onCreateAppointment = useCallback((date?: string) => {
        dispatch(entityCreating('appointments', {date, clientId: data.id, clientName: data.name} as AppointmentData))
    }, [data, dispatch])

    const onEditAppointment = useCallback((appointment: AppointmentData) => {
        dispatch(openAppointment(appointment.id))
    }, [data, dispatch])

    const onMarkAppointmentDone = useCallback((appointment: AppointmentData, hasHappened: boolean | undefined) => {
        dispatch(updateEntity(
            'appointments',
            { ...appointment, hasHappened },
            a => updateAppointmentAsync(a.id, {data: a})))
    }, [data, dispatch])

    const onEditPayment = useCallback((appointment: AppointmentData) => {
        dispatch(openAppointment(appointment.id, ['edit-payment']))
    }, [dispatch])

    const getCalendarEventColor = useCallback((appointment: AppointmentData): string => {
        const serviceColor = servicesSettings[appointment.type]?.color
        if (calendarColorMode === 'service') return serviceColor ?? appointment.professionalColor ?? '#00b07f'
        if (calendarColorMode === 'professional') return appointment.professionalColor ?? serviceColor ?? '#00b07f'
        return appointment.professionalColor ?? serviceColor ?? '#00b07f'
    }, [calendarColorMode, servicesSettings])

    const calendarData = useMemo<CalendarEvent[]>(() => appointmentsSelected.flatMap(a => {
        const startDate = parseDateOnlyToLocalDate(a.date);
        if (!startDate) return [];

        return [{
            id: a.id,
            startDate,
            endDate: new Date(startDate),
            color: getCalendarEventColor(a),
        }];
    }), [appointmentsSelected, getCalendarEventColor])

    const appointmentsWithMissingPayment = useMemo(() => canSeePrice
        ? appointments.filter(a => !a.packId && a.price > 0 && getTotalPayments(a.payments) < a.price * (1 + a.vat))
        : [], [appointments, canSeePrice])

    const appointmentsWithoutInvoice = useMemo(() => canSeeInvoice
        ? appointments.filter(a => !a.packId && a.price > 0 && !(a.invoices ?? []).length)
        : [], [appointments, canSeeInvoice])

    const handleYearChange = useCallback((evt: YearChangeEvent) => {
        setYear(evt.currentYear)
    }, [setYear])

    const handleDayClick = useCallback((evt: DayClickEvent) => {
        if (evt.events.length > 0) onEditAppointment(evt.events[0] as unknown as AppointmentData)
        else if(canEdit) onCreateAppointment(getDateString(evt.date))
    }, [canEdit, onEditAppointment, onCreateAppointment])

    useEffect(() => {
        onLoadMore(0)
    }, [onLoadMore])

    const handleLoadAppointmentChanges = useCallback((pageSize: number, offset: number) => {
        return getClientAppointmentsChangesAsync(data.id, pageSize, offset)
    }, [data.id])

    return (
        <div className="clientAppointmentsContainer">

            {(!!appointmentsWithMissingPayment.length || !!appointmentsWithoutInvoice.length) && (
                <div style={{
                    marginTop: 'var(--spacer-M)',
                    padding: 'var(--spacer-S) var(--spacer-M)',
                    background: 'var(--warning-bg)',
                    border: 'var(--border-thin) solid var(--warning-border)',
                    borderLeft: 'var(--border-thick) solid var(--warning-color)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    display: 'flex',
                    flexDirection: 'column' as const,
                    gap: 'var(--spacer-XXS)',
                }}>
                    {!!appointmentsWithMissingPayment.length && (
                        <span><b>{t(TK.missingPayment)}:</b> {appointmentsWithMissingPayment.length} {t(TK.appointments).toLowerCase()}</span>
                    )}
                    {!!appointmentsWithoutInvoice.length && (
                        <span><b>{t(TK.withoutInvoice)}:</b> {appointmentsWithoutInvoice.length} {t(TK.appointments).toLowerCase()}</span>
                    )}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacer-M)', marginBottom: 'var(--spacer-M)' }}>
                <MultiAppointmentPaymentButton clientId={data.id} appointments={appointments} />
            </div>

            <Calendar
                year={year}
                language='pt'
                style='background'
                dataSource={calendarData}
                onYearChanged={handleYearChange}
                onDayClick={handleDayClick}
            />

            <AppTabs value={activeTab} onChange={(_, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" style={{ marginTop: 'var(--spacer-M)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)' }}>
                <Tab label={t(TK.appointments)} value="appointments" />
                <Tab label={t(TK.cancellations)} value="changes" />
                <Tab label={t(TK.waitingList)} value="waitingList" />
            </AppTabs>

            <div>
                {activeTab === 'appointments' && (
                    <AppointmentsTable
                        appointments={appointmentsSelected}
                        isLoading={isLoading}
                        canEdit={canEdit}
                        settings={{hideColumns: ['client']}}
                        onLoadMore={onLoadMore}
                        onCreateAppointment={onCreateAppointment}
                        onEditAppointment={onEditAppointment}
                        onEditPayment={onEditPayment}
                        onMarkAppointmentDone={onMarkAppointmentDone}
                    />
                )}
                {activeTab === 'changes' && (
                    <AppointmentsChanges
                        clientId={data.id}
                        onLoadData={handleLoadAppointmentChanges}
                    />
                )}
                {activeTab === 'waitingList' && <ClientWaitingList isOpen={activeTab === 'waitingList'} client={data} />}
            </div>
        </div>
    )
}

export default ClientAppointments
